import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import SafeImage from '../../components/SafeImage';
import { projectService } from '../../services/projectService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [moreFromClient, setMoreFromClient] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useDocumentTitle(project?.name || "Our Projects");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchAsync = async () => {
    try {
      setError("");
      setLoading(true);
      setProject(null);
      setMoreFromClient([]);

      const response = await projectService.getBySlugAsync(slug);
      if (!response.isSuccess) {
        setError(response.errors?.[0] || "Project not found.");
        return;
      }
      setProject(response.value);

      if (response.value.clientId) {
        const siblingsResponse = await projectService.getByClientAsync(response.value.clientId);
        if (siblingsResponse.isSuccess) {
          setMoreFromClient((siblingsResponse.value || []).filter((p) => p.id !== response.value.id));
        }
      }
    } catch (err) {
      setError("Unable to load this project right now. Please try again later.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const gallery = project?.images || [];
  const services = project?.services ? project.services.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const technologies = project?.technologies ? project.technologies.split(",").map((s) => s.trim()).filter(Boolean) : [];

  return (
    <>
      <PageTitle title={project?.name || "Our Projects"} />

      <section className="blog-details pt-120 pb-120">
        <div className="container">
          {loading && (
            <div className="row">
              <div className="col-12 text-center">
                <p>Loading project...</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="row">
              <div className="col-12 text-center">
                <p>{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && project && (
            <div className="row">
              <div className="col-xl-8 col-lg-7">
                <div className="blog-details__left">
                  {project.mainImagePath && (
                    <div className="blog-details__img">
                      <SafeImage src={project.mainImagePath} alt={project.name} style={{ width: "100%" }} />
                      {project.projectYear && (
                        <div className="blog-details__date">
                          <span className="day">{project.projectYear}</span>
                          <span className="month">Year</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="blog-details__content">
                    {(project.clientName || project.projectUrl) && (
                      <ul className="list-unstyled blog-details__meta">
                        {project.clientName && (
                          <li>
                            {project.clientSlug ? (
                              <a href={`/clients/${project.clientSlug}`}><i className="fas fa-user-circle"></i> {project.clientName}</a>
                            ) : (
                              <span><i className="fas fa-user-circle"></i> {project.clientName}</span>
                            )}
                          </li>
                        )}
                        {project.projectUrl && (
                          <li>
                            <a href={project.projectUrl} target="_blank" rel="noopener noreferrer">
                              <i className="fas fa-arrow-up-right-from-square"></i> Visit Project
                            </a>
                          </li>
                        )}
                      </ul>
                    )}
                    <h3 className="blog-details__title">{project.name}</h3>
                    {project.shortDescription && <p className="blog-details__text-2">{project.shortDescription}</p>}
                    {project.fullDescription && (
                      <p className="blog-details__text-2" style={{ whiteSpace: "pre-line" }}>{project.fullDescription}</p>
                    )}
                  </div>

                  {gallery.length > 0 && (
                    <div className="mt-5">
                      <div className="sec-title mb-4">
                        <h6 className="sub-title">Photo Gallery</h6>
                        <h2 className="title">Gallery</h2>
                      </div>
                      <div className="row g-3">
                        {gallery.map((image) => (
                          <div className="col-md-4 col-6" key={image.id}>
                            <SafeImage
                              src={image.imagePath}
                              alt={project.name}
                              style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                              onClick={() => setLightboxSrc(image.imagePath)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xl-4 col-lg-5">
                <div className="sidebar">
                  {project.projectUrl && (
                    <div className="sidebar__single">
                      <a
                        href={project.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-two-rounded w-100 text-center"
                      >
                        Visit Project <i className="fa-regular fa-arrow-up-right" />
                      </a>
                    </div>
                  )}

                  {moreFromClient.length > 0 && (
                    <div className="sidebar__single sidebar__post">
                      <h3 className="sidebar__title">More From {project.clientName}</h3>
                      <ul className="sidebar__post-list list-unstyled">
                        {moreFromClient.map((p) => (
                          <li key={p.id}>
                            <div className="sidebar__post-image">
                              <SafeImage src={p.mainImagePath} alt={p.name} style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 10 }} />
                            </div>
                            <div className="sidebar__post-content">
                              <h3><a href={`/our-projects/${p.slug}`}>{p.name}</a></h3>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {services.length > 0 && (
                    <div className="sidebar__single sidebar__tags">
                      <h3 className="sidebar__title">Services</h3>
                      <div className="sidebar__tags-list">
                        {services.map((s) => (
                          <a key={s} href="#0" onClick={(e) => e.preventDefault()}>{s}</a>
                        ))}
                      </div>
                    </div>
                  )}

                  {technologies.length > 0 && (
                    <div className="sidebar__single sidebar__tags">
                      <h3 className="sidebar__title">Technologies</h3>
                      <div className="sidebar__tags-list">
                        {technologies.map((t) => (
                          <a key={t} href="#0" onClick={(e) => e.preventDefault()}>{t}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 30,
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 32,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={lightboxSrc}
            alt="Gallery enlarged"
            style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
          />
        </div>
      )}
    </>
  );
};

export default ProjectDetail;
